import { createFileRoute, Outlet } from '@tanstack/react-router';
import { useEffect } from 'react';

import { claimRememberedReferral } from '../affiliate/referral.js';
import { isUnauthenticated } from '../api/call.js';
import { useMe } from '../api/me.js';
import { CheckFailed, CheckingSession } from '../components/SessionStates.js';
import { AppFrame } from '../frame/AppFrame.js';

/**
 * Every dashboard page sits inside this layout, so the session check runs
 * before anything renders on every navigation, including Back and Forward
 * (`flow.md`, A2–A7).
 *
 * While the check is pending only the empty `aria-busy` page shows: neither
 * frame renders, so there is no flash of the wrong screen and no account data.
 * A refusal is handled by the one global `UNAUTHENTICATED` rule, which replaces
 * history with `/login?returnTo=…` (`patterns/frontend-conventions.md`, 1).
 */
export const Route = createFileRoute('/_app')({
  component: AppLayout,
});

function AppLayout() {
  const me = useMe();
  const signedIn = me.isSuccess;

  // A referral code from the sign-up link is claimed at the first log-in.
  useEffect(() => {
    if (signedIn) void claimRememberedReferral();
  }, [signedIn]);

  if (me.isPending) return <CheckingSession />;

  if (me.isError) {
    // No session: the global handler is already replacing history with the log
    // in screen, so nothing of the dashboard may render in the meantime.
    if (isUnauthenticated(me.error)) return <CheckingSession />;
    return (
      <CheckFailed
        onRetry={() => {
          void me.refetch();
        }}
      />
    );
  }

  return (
    <AppFrame accountUser={me.data}>
      <Outlet />
    </AppFrame>
  );
}
